import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DealItemUpdateInput,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { useExecuteOptimisticUpdate } from '@/lib/hooks/useExecuteOptimisticUpdate';
import { useUpdateDealItems } from '@/lib/hooks/useUpdateDealItems';
import {
  findDealItemInMap,
  isDealItemCategoryUpdating,
  isDealItemVatRateUpdating,
} from '@/lib/utils/updating-fields';
import { productsMapState } from '@/states/products.state';
import { useStore } from 'jotai';

/** Fields valid both as mutation input and in the stored active version. */
type DealItemPatch = Omit<DealItemUpdateInput, 'id'> &
  Partial<DealItemWithVersions['activeVersion']>;

export interface UpdateDealItemFieldOptions {
  /** Builds the patch from the latest known item; `null` skips the update. */
  patch: (item: DealItemWithVersions) => DealItemPatch | null;
  errorContext: string;
  /** Fields to highlight once saved. */
  modifiedFields?: string[];
}

/**
 * Saves a patch on a single deal item, optimistically, with the shared success
 * and error handling of `updateDealItemsV2`.
 *
 * The item is read from the store at call time — never subscribed to — so the
 * caller does not re-render on every products change, and an item whose
 * category or VAT rate is being updated server-side is left alone. The returned
 * function keeps a stable identity: the table memoizes its columns on it.
 */
export const useUpdateDealItemField = (dealId: string) => {
  const { t } = useTranslation();
  const store = useStore();
  const { updateProducts } = useUpdateDealItems(dealId);
  const { executeOptimisticUpdate } = useExecuteOptimisticUpdate();

  const updateImpl = async (
    id: string,
    { patch, errorContext, modifiedFields }: UpdateDealItemFieldOptions
  ): Promise<boolean> => {
    const item = findDealItemInMap(store.get(productsMapState), id);
    if (
      !item ||
      isDealItemCategoryUpdating(item) ||
      isDealItemVatRateUpdating(item)
    )
      return false;

    const fields = patch(item);
    if (!fields) return false;

    return executeOptimisticUpdate({
      optimisticUpdate: (prev) =>
        prev.map((p) =>
          p.id === item.id
            ? { ...p, activeVersion: { ...p.activeVersion, ...fields } }
            : p
        ),
      mutation: () => updateProducts([{ id: item.id, ...fields }]),
      checkSuccess: (result) =>
        result.data?.updateDealItemsV2?.__typename ===
        'MutationUpdateDealItemsSuccess',
      getErrorMessage: (result) =>
        result.data?.updateDealItemsV2?.__typename === 'DomainException'
          ? result.data.updateDealItemsV2.code
          : t('general.error_cannot_save_modifications'),
      defaultErrorMessage: t('general.error_cannot_save_modifications'),
      errorContext,
      itemIds: [item.id],
      modifiedFields,
    });
  };

  const updateRef = useRef(updateImpl);
  updateRef.current = updateImpl;

  return useCallback(
    (id: string, options: UpdateDealItemFieldOptions) =>
      updateRef.current(id, options),
    []
  );
};
